/**
 * RenderUnitTree.jsx
 *
 * Left-rail render-unit tree. Collapsible, shows status badges and
 * line-count pills. Read-only navigation — selecting a node just
 * notifies the parent which then swaps the right-pane detail view.
 *
 * Consumes the live /render-tree endpoint payload (nodeId / kind /
 * selector / status / counts / children).
 *
 * §6.6 A7 — navigation aids for real books (which can be deep):
 *   - A search box filters the tree by label OR selector. Matching
 *     branches are pruned-to-path and force-expanded, and each matching
 *     node's label is highlighted. Clearing the box restores the normal
 *     collapsible view.
 *   - Default expansion now reveals the top structural level (the root
 *     plus its immediate children — e.g. Front matter / Body) so the BC
 *     lands on a navigable tree instead of a single collapsed root, while
 *     still not pre-walking the whole (potentially huge) hierarchy.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon, Input } from "semantic-ui-react";
import { UNIT_STATUS } from "./renderingConstants";


const STATUS_CONFIG = {
  [UNIT_STATUS.COMPLETE]: { color: "s-complete", short: "✓", label: "Complete" },
  [UNIT_STATUS.PARTIAL]: { color: "s-partial", short: "◐", label: "Partial" },
  [UNIT_STATUS.NOT_RENDERED]: { color: "s-idle", short: "·", label: "Not rendered" },
  [UNIT_STATUS.STALE]: { color: "s-stale", short: "!", label: "Stale" },
};

const KIND_ICON = {
  book: "book",
  frontMatter: "list alternate outline",
  frontMatterItem: "file alternate outline",
  rearMatter: "list alternate outline",
  body: "file outline",
  bookPart: "folder open outline",
  chapter: "bookmark outline",
  announcement: "announcement",
  narration: "volume down",
  dialogue: "comments outline",
  characterInChapter: "user circle",
  characterIndex: "users",
  characterCrossBook: "user circle outline",
  eyetestGrid: "grid layout",
};

// Default expansion: reveal the root and its immediate children so the tree
// is navigable on load without pre-walking the deep hierarchy.
const collectDefaultExpanded = (root) => {
  const set = new Set();
  if (!root?.nodeId) return set;
  set.add(root.nodeId);
  (root.children || []).forEach((c) => {
    if (c?.nodeId) set.add(c.nodeId);
  });
  return set;
};

const norm = (v) => (v || "").toString().toLowerCase();

// Prune a tree to only the branches that contain a match (self or any
// descendant). Returns a new node (shallow-cloned with filtered children)
// or null when nothing in the subtree matches.
const pruneToMatches = (node, q) => {
  if (!node) return null;
  const kids = (node.children || [])
    .map((c) => pruneToMatches(c, q))
    .filter(Boolean);
  const selfMatch = norm(node.label).includes(q) || norm(node.selector).includes(q);
  if (!selfMatch && kids.length === 0) return null;
  return { ...node, children: kids };
};

const TreeNode = ({
  node,
  depth,
  selectedId,
  expanded,
  searching,
  query,
  onToggle,
  onSelect,
}) => {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  // While searching, everything rendered is on a match path — force it open.
  const isOpen = searching ? true : expanded.has(node.nodeId);
  const isSelected = selectedId === node.nodeId;
  const statusCfg = STATUS_CONFIG[node.status] || STATUS_CONFIG[UNIT_STATUS.NOT_RENDERED];
  const iconName = KIND_ICON[node.kind] || "circle outline";
  const isMatch =
    searching &&
    (norm(node.label).includes(query) || norm(node.selector).includes(query));

  return (
    <>
      <div
        className={`rut-node ${isSelected ? "is-selected" : ""} ${!node.executable ? "is-placeholder" : ""
          }`}
        style={{ paddingLeft: `${depth * 14 + 8}px` }}
        onClick={() => onSelect(node)}
        title={
          node.executable
            ? `Selector: ${node.selector}`
            : `${node.selector}\n\nNot directly executable — click to preview.`
        }
      >
        <span
          className="rut-chevron"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren && !searching) onToggle(node.nodeId);
          }}
        >
          {hasChildren ? (
            <Icon name={isOpen ? "caret down" : "caret right"} fitted />
          ) : (
            <span className="rut-chevron-spacer" />
          )}
        </span>
        <Icon name={iconName} className="rut-kind-icon" />
        <span className={`rut-label ${isMatch ? "is-match" : ""}`}>
          {node.label}
        </span>
        <span className={`rut-status-dot ${statusCfg.color}`} title={statusCfg.label}>
          {statusCfg.short}
        </span>
        <span className="rut-count">{node.counts.lines.toLocaleString()}</span>
      </div>

      {hasChildren && isOpen && (
        <div className="rut-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.nodeId}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expanded={expanded}
              searching={searching}
              query={query}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </>
  );
};

const RenderUnitTree = ({ tree, selectedId, onSelect }) => {
  const [expanded, setExpanded] = useState(() => collectDefaultExpanded(tree));
  const [rawQuery, setRawQuery] = useState("");

  // Re-seed the default-expanded set whenever the tree root changes (book
  // switch / first load), without clobbering the operator's manual toggles
  // within a single book.
  const seededRef = useRef(null);
  useEffect(() => {
    const rootId = tree?.nodeId;
    if (rootId && seededRef.current !== rootId) {
      seededRef.current = rootId;
      setExpanded(collectDefaultExpanded(tree));
    }
  }, [tree]);

  const toggle = (nodeId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  const statusLegend = useMemo(
    () => Object.values(STATUS_CONFIG).map((s) => ({ ...s })),
    []
  );

  const query = rawQuery.trim().toLowerCase();
  const searching = query.length > 0;

  // When searching, render a pruned copy of the tree; otherwise the tree
  // as-is (collapsible via `expanded`).
  const visibleTree = useMemo(
    () => (searching ? pruneToMatches(tree, query) : tree),
    [tree, searching, query]
  );

  if (!tree) return null;

  return (
    <div className="render-unit-tree">
      <div className="rut-header">
        <span className="rut-header-title">Render units</span>
        <div className="rut-legend">
          {statusLegend.map((s) => (
            <span key={s.label} className={`rut-legend-chip ${s.color}`} title={s.label}>
              {s.short}
            </span>
          ))}
        </div>
      </div>

      <div className="rut-search">
        <Input
          icon
          fluid
          size="mini"
          placeholder="Filter by name or selector…"
          value={rawQuery}
          onChange={(_, { value }) => setRawQuery(value)}
        >
          <Icon name="search" />
          <input aria-label="Filter render units" />
          {rawQuery && (
            <Icon
              name="times"
              link
              onClick={() => setRawQuery("")}
              aria-label="Clear filter"
              style={{ right: "0.5em", left: "auto" }}
            />
          )}
        </Input>
      </div>

      <div className="rut-body">
        {searching && !visibleTree ? (
          <div className="rut-no-results">
            <Icon name="search" /> No render units match “{rawQuery}”.
          </div>
        ) : (
          <TreeNode
            node={visibleTree}
            depth={0}
            selectedId={selectedId}
            expanded={expanded}
            searching={searching}
            query={query}
            onToggle={toggle}
            onSelect={onSelect}
          />
        )}
      </div>
    </div>
  );
};

export default RenderUnitTree;
